import {
  createContext,
  useContext,
  type KeyboardEvent,
  type ReactNode,
  type ThHTMLAttributes,
} from 'react';

import { cx } from './cx';

/**
 * The console's one data table: 13px, caps headers, a rule between rows.
 *
 * `tight` is the variant for a table with more columns than a card is wide — headers wrap
 * and sit on their baselines, cells lose 2px of padding. It travels by context rather than
 * as a prop on every cell, because a table where half the cells were told and half were not
 * is exactly the sort of wrong that survives review.
 */
const Tight = createContext(false);

export function Table({
  tight,
  className,
  children,
}: {
  tight?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <Tight.Provider value={tight ?? false}>
      <table className={cx('w-full border-collapse text-table', className)}>{children}</table>
    </Tight.Provider>
  );
}

export function Th({
  numeric,
  className,
  children,
  ...rest
}: {
  numeric?: boolean;
  className?: string;
  children?: ReactNode;
} & Omit<ThHTMLAttributes<HTMLTableCellElement>, 'className' | 'children'>) {
  const tight = useContext(Tight);

  return (
    <th
      {...rest}
      className={cx(
        'border-b border-rule text-caps font-semibold tracking-[0.06em] uppercase text-ink-3',
        tight ? 'px-2.5 py-1.5 leading-[1.25] align-bottom whitespace-normal' : 'px-3 py-[7px] whitespace-nowrap',
        numeric ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  header,
  numeric,
  wrap,
  colSpan,
  className,
  children,
}: {
  /**
   * The cell that says which row this is — a driver's name, a date, a zone.
   *
   * Rendered as `<th scope="row">`, which is what lets a screen reader say "Georgi D.,
   * charged, 42" instead of reading a row of bare numbers. It is a data cell in every other
   * respect and takes none of `Th`'s caps styling: a column header and a row header look
   * nothing alike and only one of them is a label.
   */
  header?: boolean;
  numeric?: boolean;
  /** A cell whose content may run onto a second line — an address, mostly. */
  wrap?: boolean;
  colSpan?: number;
  className?: string;
  children?: ReactNode;
}) {
  const tight = useContext(Tight);

  const look = cx(
    'border-b border-rule align-middle font-normal text-left',
    tight ? 'px-2.5 py-[7px]' : 'px-3 py-2',
    wrap ? 'whitespace-normal' : 'whitespace-nowrap',
    numeric ? 'text-right' : '',
    className,
  );

  if (header) {
    return (
      <th scope="row" colSpan={colSpan} className={look}>
        {children}
      </th>
    );
  }

  return (
    <td colSpan={colSpan} className={look}>
      {children}
    </td>
  );
}

/**
 * A row. `selected` is the brand wash — the one the dispatcher's eye returns to after
 * looking at the phone — and `total` is the summed row at the foot of a report.
 *
 * With `onClick` it is focusable and Enter or Space on it does the same, unless the caller
 * passes its own `onKeyDown`.
 */
export function Tr({
  selected,
  total,
  onClick,
  onKeyDown,
  className,
  children,
}: {
  selected?: boolean;
  total?: boolean;
  onClick?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLTableRowElement>) => void;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <tr
      onClick={onClick}
      onKeyDown={
        onKeyDown ??
        (onClick
          ? (event) => {
              // A row that can be clicked can be focused, so it can be chosen from the keys
              // too — Enter or Space on the row itself, never on a button inside it, which
              // has its own.
              if (event.target !== event.currentTarget) return;
              if (event.key !== 'Enter' && event.key !== ' ') return;

              event.preventDefault();
              onClick();
            }
          : undefined)
      }
      tabIndex={onClick ? 0 : undefined}
      aria-selected={onClick ? selected : undefined}
      className={cx(
        // Both cell elements: a row's first cell is a `<th scope="row">` where the row is
        // about something — a driver, a date, a zone — and it must take the same wash.
        selected
          ? '[&>td]:bg-brand-soft [&>th]:bg-brand-soft'
          : total
            ? '[&>td]:bg-surface-2 [&>td]:font-semibold [&>td]:border-b-0 [&>th]:bg-surface-2 [&>th]:font-semibold [&>th]:border-b-0'
            : onClick
              ? 'hover:[&>td]:bg-surface-2 hover:[&>th]:bg-surface-2'
              : '',
        onClick ? 'cursor-pointer' : '',
        className,
      )}
    >
      {children}
    </tr>
  );
}

/**
 * The second line inside a cell: a destination under a pickup, a call sign under a name.
 * `block` puts it on its own line; without it the two sit side by side.
 */
export function Sub({
  block,
  className,
  /** The whole text, for a line narrow enough to be truncated. */
  title,
  children,
}: {
  block?: boolean;
  className?: string;
  title?: string;
  children?: ReactNode;
}) {
  return (
    <small
      title={title}
      className={cx(
        'text-ink-3 font-normal',
        block ? 'block leading-[1.3] text-[11.5px]' : 'text-[12px]',
        className,
      )}
    >
      {children}
    </small>
  );
}
